import type { Endpoint, Remote } from "comlink";
import * as Comlink from "comlink";
import type {
  CreateDocumentOptions,
  ExportOptions,
  IAdobeHost,
  PlaceImageOptions,
} from "../host/types";

/**
 * Implements IAdobeHost by proxying all calls to a remote PhotoshopHost
 * running in the UXP shell via Comlink over postMessage.
 *
 * The `appName` property is set synchronously at construction since
 * Comlink.wrap() would make it async (Remote<IAdobeHost> turns properties
 * into Promise<T>). The workflow hooks expect synchronous access.
 */
export class PostMessageHost implements IAdobeHost {
  readonly appName = "photoshop" as const;
  private remote: Remote<IAdobeHost>;

  constructor(endpoint: Endpoint) {
    this.remote = Comlink.wrap<IAdobeHost>(endpoint);
  }

  createDocument(opts: CreateDocumentOptions): Promise<void> {
    return this.remote.createDocument(opts);
  }

  placeImage(localPath: string, opts?: PlaceImageOptions): Promise<void> {
    return this.remote.placeImage(localPath, opts);
  }

  exportAsPng(
    outputDir: string,
    filename: string,
    opts?: ExportOptions,
  ): Promise<string> {
    return this.remote.exportAsPng(outputDir, filename, opts);
  }

  saveNativeFile(outputDir: string, filename: string): Promise<string> {
    return this.remote.saveNativeFile(outputDir, filename);
  }

  getTempDirectory(): Promise<string> {
    return this.remote.getTempDirectory();
  }

  writeFile(filePath: string, data: ArrayBuffer): Promise<void> {
    return this.remote.writeFile(filePath, Comlink.transfer(data, [data]));
  }

  readFileAsArrayBuffer(filePath: string): Promise<ArrayBuffer> {
    return this.remote.readFileAsArrayBuffer(filePath);
  }

  hasOpenDocument(): Promise<boolean> {
    return this.remote.hasOpenDocument();
  }

  getActiveDocumentName(): Promise<string | null> {
    return this.remote.getActiveDocumentName();
  }
}
